<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			:introduction-content="page.introductionContent"
		/>

		<section class="main-content review-section background-light">
			<review-card v-for="review in pagedReviews" :key="review.ID" :item="review" class="row review-row" />
			<nav v-if="totalPages > 1" class="pagination" aria-label="Reviews pagination">
				<div class="pagination-button-container">
					<button
						class="slider-button slider-prev"
						:disabled="currentPage === 1"
						@click="currentPage--"
					></button>

					<ul class="page-list" role="list">
						<li v-for="n in totalPages" :key="n">
							<button
								class="page-btn number"
								:class="{ active: n === currentPage }"
								:aria-current="n === currentPage ? 'page' : undefined"
								@click="currentPage = n"
							>
								{{ n }}
							</button>
						</li>
					</ul>
					<button
						class="slider-button slider-next"
						:disabled="currentPage === totalPages"
						@click="currentPage++"
					></button>
				</div>
			</nav>
		</section>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:contentblocks="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:subtitle="page.frequentlyAskedQuestion.subtitle"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer
			v-if="page && page.footerSection && page.footerSection.length"
			:footer-section="page.footerSection[0]"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { fetchDefaults } = useDefaults();
const { locale } = useI18n();
const config = useRuntimeConfig();

await fetchPage();
await fetchDefaults();

const { data: reviews } = await useWebsiteFetch('reviews', {
	query: { language: locale.value },
	key: `${locale.value}/reviews`,
});

const PAGE_SIZE = 10;
const currentPage = ref(1);

const totalItems = computed(() => reviews.value?.length || 0);
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / PAGE_SIZE)));

const pagedReviews = computed(() => {
	const start = (currentPage.value - 1) * PAGE_SIZE;

	return (reviews.value || []).slice(start, start + PAGE_SIZE);
});

watch(totalPages, () => {
	if (currentPage.value > totalPages.value) {
		currentPage.value = totalPages.value;
	}
});

const getReviewTotalAverage = () => {
	const items = (reviews.value || []).filter((r) => r.rating);
	const sum = items.reduce((t, r) => t + parseInt(r.rating, 10), 0);

	return items.length ? sum / items.length : 0;
};

const currentUrl = config.public.siteUrl;

useHead({
	script: [
		{
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@context': 'http://schema.org',
				'@type': 'AggregateRating',
				'@id': `${currentUrl}#aggregaterating`,
				bestRating: 10,
				ratingValue: getReviewTotalAverage(),
				ratingCount: reviews.value?.length || 0,
				itemReviewed: { '@id': `${currentUrl}#hotel` },
			}),
		},
	],
});
</script>

<style lang="scss" scoped>
.review-row {
	margin: 0 auto 25px;
	max-width: 900px;
}

.pagination {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	margin: 20px auto 0;
	position: relative;
	width: 20%;

	.pagination-button-container {
		display: flex;
		align-items: center;
	}

	.slider-button {
		position: absolute;
	}

	.slider-button.slider-prev {
		left: 10px;
	}

	.slider-button.slider-next {
		right: 10px;
		background: url('~/assets/images/slider-nav-button-next.png') no-repeat center center;
		background-size: 57px;
	}

	.slider-button::after {
		content: '';
		display: block;
		background: var(--cta-color);
		opacity: 0;
		transition: all 0.3s ease-in-out;
		mix-blend-mode: color;
		position: absolute;
		inset: 1px 5px 5px 1px;
		border-radius: 50%;
	}

	.slider-button.slider-next::after {
		inset: 1px 1px 5px 5px;
	}

	.slider-button:hover::after {
		opacity: 1;
	}

	.slider-button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		pointer-events: none;
	}

	.page-list {
		display: flex;
		gap: 8px;
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.page-btn {
		border: none;
		background: none;
		padding: 8px 12px;
		border-radius: 90px;
		cursor: pointer;
		font-size: 14px;
		line-height: 1;

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		&.number.active {
			border-color: #222;
			background: var(--cta-color);
			color: #222;
		}
	}
}
</style>
